/**
 * Perceived brightness (0 to 1) of an `rgb()` or `rgba()` computed color, or
 * undefined for a transparent or unparsed one. Exported for tests.
 */
export const backgroundBrightness = (color: string): number | undefined => {
	const [red = 0, green = 0, blue, alpha = 1] = (
		color.startsWith("rgb") ? (color.match(/[\d.]+/gu) ?? []) : []
	).map(Number);
	return blue === undefined || alpha < 0.5
		? undefined
		: (0.2126 * red + 0.7152 * green + 0.0722 * blue) / 255;
};

/**
 * Whether the host page reads as light or dark, so the widget can match it
 * instead of the visitor's OS setting – a dark panel on a white site looks
 * broken. A declared dark-only `color-scheme` wins; otherwise the page
 * background's brightness decides, and a transparent page is the browser's
 * white. Every byte here ships on every host page load, so this stays
 * minimal.
 */
export const hostTheme = (): "dark" | "light" => {
	const scheme = getComputedStyle(document.documentElement).colorScheme;
	if (scheme.includes("dark") && !scheme.includes("light")) {
		return "dark";
	}
	const brightness =
		backgroundBrightness(getComputedStyle(document.body).backgroundColor) ??
		backgroundBrightness(
			getComputedStyle(document.documentElement).backgroundColor,
		);
	return brightness !== undefined && brightness < 0.5 ? "dark" : "light";
};
